import { elapsedMs, enumeration, formatDuration, type GameState, type PuzzleModel } from '@arrow/game-engine';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import type { Snapshot } from '../game/useGame.ts';
import type { ShareOutcome } from '../game/share.ts';
import { clueForAnswer, clueText } from '../game/view.ts';
import { busyButton } from './busy.ts';
import { DirectionGlyph, Icon, Swords } from './icons.tsx';
import { shareStatus } from './ResultSheet.tsx';
import { Toast, useToast } from '../ui/index.ts';

interface ClueBarProps {
	model: PuzzleModel;
	snap: Snapshot;
	onStep: (delta: 1 | -1) => void;
	onToggle: () => void;
	onOpenCard: () => void;
	onDismissHint: () => void;
	/** A one-off line as the game opens ("You’re in Family"): the band's slip, said once. */
	notice?: string | null | undefined;
}

const noFocusSteal = (e: React.PointerEvent) => e.preventDefault();

/**
 * The direction control's name says where you are and what it does ("Down. Switch to
 * across"), on the band's tag and the drawn keyboard's swap key alike (UX-037).
 */
export function switchLabel(axis: 'across' | 'down' | null | undefined): string {
	if (!axis) return 'Switch direction';
	return axis === 'down' ? 'Down. Switch to across' : 'Across. Switch to down';
}

/**
 * The active clue on a full-bleed ink band: a vermilion tag carrying the answer's
 * direction (tap it to switch direction), the clue in soft italic, its length in caps,
 * and previous / next.
 */
export function ClueBar({ model, snap, onStep, onToggle, onOpenCard, onDismissHint, notice }: ClueBarProps) {
	const noticeToast = useToast({ place: 'over-band' });
	const { show } = noticeToast;
	useEffect(() => {
		if (notice) show(notice);
	}, [notice, show]);
	const { game } = snap;
	const done = game.completedAt !== null;
	const answerId = game.cursor?.answerId;
	const clue = answerId ? clueForAnswer(model, answerId) : undefined;
	const answer = answerId ? model.answers[answerId] : undefined;
	// Solves and Flow are shown on the board; only check results are worded here.
	const toast = snap.toast?.kind === 'check' ? snap.toast : null;
	const text = answerId ? clueText(model, answerId) : 'Tap a clue to begin';
	const clueRef = useFitBand(text);

	return (
		<div className="cluebar-wrap">
			{/* Check results: the one slip, over the band (its words go to the game's live region). */}
			{toast ? <Toast key={toast.serial} text={toast.text} place="over-band" /> : noticeToast.node}
			<div className="cluebar">
				{snap.hint && (
					<button type="button" className="slip cb-hint" onClick={onDismissHint}>
						{/* For one beat, in the band's own space, never over the board (UX-035). One
						    shows, by input (controls.css): with a mouse and keyboard, the grid's keys. */}
						<span className="hint-touch">Type your answer. Tap again to switch direction.</span>
						<span className="hint-keys">Type your answer. Tab: next clue · Space: switch direction · Esc: leave the grid.</span>
					</button>
				)}
				{done ? (
					<DoneBar game={game} onOpenCard={onOpenCard} />
				) : (
					<>
						<button
							type="button"
							className="cb-tag"
							aria-label={switchLabel(answer?.axis)}
							onPointerDown={noFocusSteal}
							onClick={onToggle}
							disabled={!clue}
						>
							{clue && <DirectionGlyph direction={clue.direction} size={24} />}
						</button>
						<div className="cb-main">
							{/* The length is the crossword's own enumeration, "(3)", set after the clue
							    (and kept on its line by a no-break space) rather than a separate label. */}
							{/* A div, not a paragraph: user text-spacing styles add paragraph spacing,
							    which would push the first line out of the fixed band (QA4-12). */}
							<div ref={clueRef} className="cb-clue" title={text}>
								<span className="cb-text">{text}</span>
								{answer && (
									<span className="cb-len">
										<span aria-hidden="true">{`\u00a0(${enumeration(answer).label})`}</span>
										<span className="sr-only">, {enumeration(answer).spoken}</span>
									</span>
								)}
							</div>
						</div>
						<div className="cb-nav">
							<button type="button" className="nav" aria-label="Previous clue" onPointerDown={noFocusSteal} onClick={() => onStep(-1)}>
								<Icon.prev />
							</button>
							<button type="button" className="nav" aria-label="Next clue" onPointerDown={noFocusSteal} onClick={() => onStep(1)}>
								<Icon.next />
							</button>
						</div>
					</>
				)}
			</div>
		</div>
	);
}

const SIZES = ['m', 'l', 'xl'] as const;

/**
 * The band is a fixed height (controls.css, UX-031), so the clue fits it rather than the
 * other way round: the largest size whose whole clue fits the band, else the smallest,
 * clamped to two lines. Measured before paint, and again when the band's width changes.
 */
function useFitBand(text: string) {
	const ref = useRef<HTMLDivElement>(null);
	useLayoutEffect(() => {
		const el = ref.current;
		const box = el?.parentElement;
		if (!el || !box) return;
		const fit = () => {
			const cs = getComputedStyle(box);
			const room = box.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
			if (!(room > 0)) {
				// No layout (tests): the old guess by length.
				el.dataset.size = clueSize(text);
				return;
			}
			el.dataset.measure = '';
			let size: (typeof SIZES)[number] = 'xl';
			for (const s of SIZES) {
				el.dataset.size = s;
				if (el.scrollHeight <= room + 1) {
					size = s;
					break;
				}
			}
			el.dataset.size = size;
			delete el.dataset.measure;
		};
		fit();
		let live = true;
		document.fonts?.ready.then(() => live && fit());
		const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(fit);
		ro?.observe(box);
		return () => {
			live = false;
			ro?.disconnect();
		};
	}, [text]);
	return ref;
}

/** Long clues step down a size before they ever wrap (and balance if they still do). */
function clueSize(text: string): 'm' | 'l' | 'xl' {
	return text.length > 30 ? 'xl' : text.length > 22 ? 'l' : 'm';
}

function DoneBar({ game, onOpenCard }: { game: GameState; onOpenCard: () => void }) {
	return (
		<>
			<span className="cb-tag done" aria-hidden="true">
				<Icon.check />
			</span>
			<div className="cb-main">
				<div className="cb-clue">
					<span className="cb-text">Solved in {formatDuration(elapsedMs(game, game.completedAt ?? 0))}</span>
				</div>
			</div>
			<button type="button" className="text-btn" onClick={onOpenCard}>
				See result
			</button>
		</>
	);
}


interface FinishedFootProps {
	model: PuzzleModel;
	game: GameState;
	/** The answer being read back (its clue tapped), or null. */
	reviewId: string | null;
	onOpenCard: () => void;
	/** The share, and whether a dare went without its link (`plain`): the foot says so, as the poster does. */
	onShare: () => Promise<{ outcome: ShareOutcome; plain: boolean }>;
	/** "Challenge" (a daily: the dare), "Rematch" (after a challenge), or "Share" (practice). */
	shareLabel?: string;
	/** The dare: the vermilion button. */
	dare?: boolean;
	/** The read-back line. Off in the wide game, where the clue list beside it shows every clue and answer. */
	review?: boolean;
}

/**
 * The finished board's foot, in place of the clue band and keyboard (nothing is left to
 * type): a slim read-only line that reads back whichever clue is tapped, with its answer,
 * then "See result" (the poster again) and Share, side by side. The solve time is the
 * masthead's stopped clock, so it isn't repeated here. A colophon under the
 * puzzle, not a toolbar.
 */
export function FinishedFoot({ model, game, reviewId, onOpenCard, onShare, shareLabel = 'Share', dare = false, review = true }: FinishedFootProps) {
	const [busy, setBusy] = useState(false);
	// "Copied" and friends: the one slip, over the foot's rule (UX-008).
	const toast = useToast({ place: 'over-band' });
	const share = async () => {
		setBusy(true);
		const { outcome, plain } = await onShare().finally(() => setBusy(false));
		const text = shareStatus(outcome, plain);
		if (text) toast.show(text);
	};
	return (
		<section className="foot" aria-label="Finished puzzle">
			{toast.node}
			{review && <ReviewLine model={model} game={game} answerId={reviewId} />}
			{/* The time is the masthead's clock, stopped. The share leads (the dare in red, with
			    the challenge's swords); See result is the quieter way back to the poster (UX-036). */}
			<div className="foot-row">
				<div className="foot-actions">
					<button type="button" className="btn md secondary fit" onClick={onOpenCard}>
						See result
					</button>
					<button type="button" className={dare ? 'btn md dare foot-share' : 'btn md primary foot-share'} {...busyButton(busy, () => void share())}>
						{dare && <Swords />}
						{shareLabel}
						{!dare && (
							<svg className="wedge" viewBox="0 0 24 24" aria-hidden="true">
								<path d="M2 9.2h10.6V3.6L22 12l-9.4 8.4v-5.6H2z" transform="rotate(-45 12 12)" />
							</svg>
						)}
					</button>
				</div>
			</div>
		</section>
	);
}

/** The clue read back on a finished board: its direction, the clue, and the answer. */
function ReviewLine({ model, game, answerId }: { model: PuzzleModel; game: GameState; answerId: string | null }) {
	const clue = answerId ? clueForAnswer(model, answerId) : undefined;
	const answer = answerId ? model.answers[answerId] : undefined;
	if (!answerId || !clue || !answer) {
		return (
			<p className="review idle">
				<span className="rv-clue">Tap a clue to read it back</span>
			</p>
		);
	}
	const text = clueText(model, answerId);
	const letters = answer.cells.map((c) => game.entries[c] || '·').join('');
	return (
		<p className="review" data-size={clueSize(text)}>
			<span className="rv-tag" aria-hidden="true">
				<DirectionGlyph direction={clue.direction} size={16} />
			</span>
			<span className="rv-clue" title={text}>
				{text}
			</span>
			<span className="rv-ans">
				<span className="sr-only">, answer </span>
				{letters}
			</span>
		</p>
	);
}
